import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { PrintOptionsForm, useLiveQuote, DEFAULT_OPTS } from "@/components/PrintOptionsForm";
import { DocumentCard } from "@/components/DocumentCard";
import { DEFAULT_DOC_OPTS, computeLinePrice } from "@/lib/pricing";
import { api, apiError, API } from "@/lib/api";
import { Loader2, CheckCircle2, FileText, HelpCircle, AlertTriangle } from "lucide-react";

export default function AnswerQuestions() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setError("Invalid link."); return; }
    api.get(`/answer/${token}`).then(({ data }) => {
      setJobs(data.jobs && data.jobs.length ? data.jobs : [data.job]);
    }).catch((e) => setError(apiError(e)));
  }, [token]);

  if (error) return <Center><p className="text-slate-600" data-testid="answer-error">{error}</p></Center>;
  if (done) return <SuccessView nav={nav} cancelled={done === "cancelled"} />;
  if (!jobs) return <Center><Loader2 className="animate-spin text-brand" size={32} /></Center>;

  return jobs.length > 1
    ? <MultiAnswer token={token} jobs={jobs} onDone={setDone} />
    : <SingleAnswer token={token} job={jobs[0]} onDone={setDone} />;
}

function SingleAnswer({ token, job, onDone }) {
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [price, setPrice] = useState(null);
  const [currency, setCurrency] = useState("€");
  const [notes, setNotes] = useState("");
  const [printBody, setPrintBody] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const pages = job.pages;
  const pagesKnown = job.page_count_known;
  const isBody = job.is_email_body;

  useLiveQuote(opts, setPrice, setCurrency, pages || 1);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/answer/${token}`, {
        copies: Math.max(1, Number(opts.copies) || 1), color: opts.color, size: opts.size, paper: opts.paper,
        sided: opts.sided, pickup_asap: opts.pickup_asap,
        pickup_datetime: opts.pickup_asap ? null : opts.pickup_datetime,
        print_body: isBody ? printBody : true, notes,
      });
      onDone(isBody && !printBody ? "cancelled" : "done");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showOptions = !isBody || printBody;

  return (
    <Shell>
      <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Print request details</span>
      <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-3">A few quick questions</h1>
      <div className="mt-4 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
        <FileText className="text-brand" size={18} />
        <span className="font-semibold text-ink">{job.title}</span>
        <span className="font-mono text-sm text-slate-400">· {job.job_number}</span>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-8">
        {isBody && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="email-body-block">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-semibold text-ink">There were no files attached to your email.</p>
                <p className="text-sm text-slate-600 mt-1">Do you want us to print the body of your email instead? Here's a preview:</p>
              </div>
            </div>
            {job.has_file && (
              <iframe src={`${API}/answer/${token}/file/${job.id}`} title="Email body preview"
                data-testid="email-body-preview" className="w-full h-80 mt-4 rounded-lg border border-amber-200 bg-white" />
            )}
            <div className="flex gap-3 mt-4">
              <button type="button" data-testid="print-body-yes" onClick={() => setPrintBody(true)}
                className={`px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${printBody ? "border-brand bg-white text-brand" : "border-slate-200 bg-white text-slate-600 hover:bg-white"}`}>Yes, print it</button>
              <button type="button" data-testid="print-body-no" onClick={() => setPrintBody(false)}
                className={`px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${!printBody ? "border-red-400 bg-white text-red-600" : "border-slate-200 bg-white text-slate-600 hover:bg-white"}`}>No, don't print</button>
            </div>
          </div>
        )}

        {showOptions && (
          <PrintOptionsForm opts={opts} setOpts={setOpts} price={price} currency={currency} pages={pages} pagesKnown={pagesKnown} />
        )}

        <div>
          <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Notes / special requests</label>
          <textarea data-testid="answer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Anything we should know? e.g. staple top-left, urgent…"
            className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand resize-y" />
        </div>

        <button type="submit" disabled={submitting} data-testid="answer-submit-button"
          className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : (isBody && !printBody ? "Confirm — don't print" : "Confirm details")}
        </button>
      </form>
    </Shell>
  );
}

function MultiAnswer({ token, jobs, onDone }) {
  const [pricing, setPricing] = useState(null);
  const [docs, setDocs] = useState(
    jobs.map((j) => ({
      id: j.id, job_number: j.job_number, file: { name: j.title },
      pages: j.pages, pagesKnown: j.page_count_known, counting: false,
      opts: { ...DEFAULT_DOC_OPTS },
    }))
  );
  const [pickupAsap, setPickupAsap] = useState(true);
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);
  const currency = pricing?.currency_symbol || "€";
  const total = docs.reduce((s, d) => s + computeLinePrice(pricing, d.opts, d.pages || 1), 0);
  const updateDoc = (next) => setDocs((ds) => ds.map((d) => (d.id === next.id ? next : d)));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/answer/${token}/order`, {
        items: docs.map((d) => ({
          job_id: d.id, copies: Math.max(1, Number(d.opts.copies) || 1),
          color: d.opts.color, size: d.opts.size, paper: d.opts.paper, sided: d.opts.sided,
        })),
        pickup_asap: pickupAsap,
        pickup_datetime: pickupAsap ? null : pickupDate,
        notes,
      });
      onDone("done");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell wide>
      <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Print request details</span>
      <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-3">A few quick questions</h1>
      <p className="text-slate-600 mt-2">You sent <b>{docs.length} files</b>. Set the options for each one below — the estimated total updates as you go.</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="order-answer-form">
        <div className="space-y-4" data-testid="answer-docs-list">
          {docs.map((d) => (
            <DocumentCard key={d.id} doc={d} pricing={pricing} currency={currency} onChange={updateDoc} />
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-3">Pick-up time (applies to all files)</label>
          <div className="flex gap-3">
            <button type="button" data-testid="answer-pickup-asap" onClick={() => setPickupAsap(true)}
              className={`px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${pickupAsap ? "border-brand bg-orange-50 text-brand" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>ASAP</button>
            <button type="button" data-testid="answer-pickup-scheduled" onClick={() => setPickupAsap(false)}
              className={`px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${!pickupAsap ? "border-brand bg-orange-50 text-brand" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Schedule</button>
            {!pickupAsap && (
              <input type="datetime-local" data-testid="answer-pickup-datetime" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                className="px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none text-ink" />
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Notes / special requests</label>
          <textarea data-testid="answer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Anything we should know? e.g. staple each set, urgent…"
            className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand resize-y" />
        </div>

        <div className="rounded-xl bg-ink text-white px-5 py-4 flex items-center justify-between" data-testid="answer-order-total">
          <div>
            <span className="uppercase text-xs tracking-[0.18em] text-slate-300 block">Estimated total</span>
            <span className="text-xs text-slate-300 mt-0.5 block">{docs.length} documents · final price confirmed at pickup</span>
          </div>
          <span className="font-heading font-extrabold text-2xl">{currency}{total.toFixed(2)}</span>
        </div>

        <p className="text-xs text-slate-500 flex items-start gap-1.5">
          <HelpCircle size={13} className="mt-0.5 shrink-0" /> This is an estimate based on the files sent. The final price is confirmed in person when you pick up your printing.
        </p>

        <button type="submit" disabled={submitting} data-testid="answer-order-submit-button"
          className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : `Confirm details for ${docs.length} files`}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className={`${wide ? "max-w-4xl" : "max-w-3xl"} mx-auto px-6 h-16 flex items-center`}><Logo /></div>
      </header>
      <main className={`${wide ? "max-w-4xl" : "max-w-3xl"} mx-auto px-6 py-10 md:py-14`}>{children}</main>
    </div>
  );
}

function SuccessView({ nav, cancelled }) {
  return (
    <Center>
      <div className="text-center max-w-md animate-fade-up" data-testid="answer-success">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-green-50 text-green-600 mx-auto mb-6"><CheckCircle2 size={34} /></span>
        <h1 className="font-heading font-black text-3xl text-ink">Thank you!</h1>
        <p className="text-slate-600 mt-3">
          {cancelled
            ? "No problem — we won't print anything for that email. If you'd like something printed, just email it to us with an attachment."
            : "Your details are in and your job is now queued for printing. We've emailed you a confirmation with a tracking link."}
        </p>
        <button onClick={() => nav("/login")} className="mt-6 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-lg transition-colors">Track my jobs</button>
      </div>
    </Center>
  );
}

function Center({ children }) {
  return <div className="min-h-screen grid place-items-center px-6">{children}</div>;
}
